// The producer's library, as every surface edits it: folders, moving, tags,
// stars, smart folders, and files dropped in — with no room in sight. The
// room's shelf (useMineShelf) adds placing on top; the library page
// (pages/LibraryPage.tsx) uses it as it is.
//
// Dropped files go up through useClipSender: into the folder on screen, a
// dropped folder keeping its shape as library folders.

import { useCallback, useMemo } from "react";

import { type CreateSmartFolderRequest, type KeepClipRequest, type KeepClipResponse, type MyClip, type MyFolder, type MyMidi, type MySmartFolder, type TagDelta, type UpdateClipRequest } from "@shared/api-types.js";
import { LIBRARY_TAG_SUGGESTIONS_MAX } from "@shared/library-tags.js";
import { track } from "@web-shared/lib/analytics";
import { json, webApi } from "@web-shared/lib/web-api";
import type { MaterialPayload } from "@web-shared/lib/material-item";
import { folderPath, referenceMidiOf } from "@web-shared/lib/material-item";
import { suggestFromMidi } from "@web-shared/lib/tags/midi-key";
import { suggestFromFolder } from "@web-shared/lib/tags/suggest-name";
import type { MineShelf } from "@/components/MaterialLibrary";
import { useAuthGate } from "../auth";
import { useReferenceLibrary } from "../components/ReferenceClips";
import { useClipSender, type ClipSender } from "./useClipSender";
import { useMyClips, usageText } from "./useMyClips";

/** What the room adds on top of the library: where a row lands. */
export type MineLibrary = Omit<MineShelf, "onPlace" | "onPlaceMidi" | "onSaveMidi" | "onSaveAudio" | "onPlaceReference" | "placeTarget" | "swapTarget" | "onSwap">;

/**
 * What a folder's name and its ancestors' say, as tags — nearest first,
 * capped at what a new item may carry (shared/library-tags.ts
 * LIBRARY_TAG_SUGGESTIONS_MAX = 8).
 */
export function folderTags(folders: readonly MyFolder[], folderId: string): string[] {
  const out: string[] = [];
  for (const f of folderPath(folders, folderId).reverse()) for (const t of suggestFromFolder(f.name)) if (!out.includes(t.tag)) out.push(t.tag);
  return out.slice(0, LIBRARY_TAG_SUGGESTIONS_MAX);
}

export function useMineLibrary(): {
  mine: ReturnType<typeof useMyClips>;
  library: MineLibrary;
  setDropProblem: (text: string | null) => void;
  /** A seed's or a jam's clip, kept into Mine; the clip, or null when it could not be. */
  keep: (req: KeepClipRequest) => Promise<MyClip | null>;
  /** A seed's MIDI clip (by its reference key), kept as a library idea. */
  keepMidi: (seedId: string, key: string, folderId: string | null) => Promise<MyMidi | null>;
  /** The way clips go up from here — handed to an upload flow opened on the same page. */
  sender: ClipSender;
} {
  const mine = useMyClips();
  const reference = useReferenceLibrary();
  const seeds = useMemo(() => reference.data?.seeds ?? [], [reference.data]);
  /** A seed's MIDI clip as an idea of the library: its notes, the seed's
   * tempo, and what the notes say they are (a key, or which drums). */
  const keepMidi = useCallback(
    async (seedId: string, key: string, folderId: string | null): Promise<MyMidi | null> => {
      const seed = seeds.find((s) => s.id === seedId);
      const midi = seed?.midi?.find((m) => m.key === key);
      if (!seed || !midi) return null;
      let tags: ReturnType<typeof suggestFromMidi> = [];
      try {
        tags = suggestFromMidi(midi.notes);
      } catch {
        tags = [];
      }
      try {
        const made = await mine.addMidi({ name: midi.name, folderId, notes: midi.notes, durationBeats: midi.durationBeats, bpm: seed.bpm > 0 ? seed.bpm : null, ...(tags.length > 0 ? { tags } : {}) });
        track("library_midi_saved", { detail: "seed", value: 1 });
        return made;
      } catch {
        return null;
      }
    },
    [seeds, mine],
  );
  /** Keep a clip the server already holds (a seed's, or a jam's) in Mine. */
  const keep = useCallback(
    async (req: KeepClipRequest): Promise<MyClip | null> => {
      try {
        const res = await webApi<KeepClipResponse>("/library/clips/keep", json("POST", req));
        if (!res.already) mine.added([res.clip], res.usedBytes);
        track("library_clip_kept", { detail: res.already ? "already" : "new", value: 1 });
        return res.clip;
      } catch {
        return null;
      }
    },
    [mine],
  );
  const gate = useAuthGate();
  // Files handed over, folders made for them, the upload and its progress
  // line: the one way clips go up, shared with the upload flow's clips form.
  const sender = useClipSender(mine);
  const { setDropProblem, createFolder } = sender;

  // ---- Folders ----------------------------------------------------------------


  const moveItems = useCallback(
    (items: readonly MaterialPayload[], folderId: string | null) => {
      const clipIds: string[] = [];
      const midiIds: string[] = [];
      for (const p of items) {
        if ("mine" in p) clipIds.push(p.mine);
        else if ("mineMidi" in p) midiIds.push(p.mineMidi);
      }
      if (clipIds.length + midiIds.length === 0) return;
      // A row moved into a folder hears what the folder's name says — as
      // suggestions, once the move has landed (the same call cannot do both).
      const tags = folderId ? folderTags(mine.folders, folderId) : [];
      void mine.move({ clipIds, midiIds, folderId }).then(() => {
        if (tags.length > 0) void mine.tagMany({ clipIds, midiIds, suggest: tags });
      });
      track("library_moved", { value: clipIds.length + midiIds.length });
    },
    [mine],
  );

  /** The folder's derived tags as suggestions on the rows (the folder row's "Suggest tags"). */
  const suggestMany = useCallback(
    (payloads: readonly MaterialPayload[], tags: readonly string[]) => {
      const clipIds = payloads.flatMap((p) => ("mine" in p ? [p.mine] : []));
      const midiIds = payloads.flatMap((p) => ("mineMidi" in p ? [p.mineMidi] : []));
      if (clipIds.length + midiIds.length === 0 || tags.length === 0) return;
      for (let i = 0; i < Math.max(clipIds.length, midiIds.length); i += 500) {
        void mine.tagMany({ clipIds: clipIds.slice(i, i + 500), midiIds: midiIds.slice(i, i + 500), suggest: [...tags] });
      }
      track("library_folder_suggested", { value: clipIds.length + midiIds.length });
    },
    [mine],
  );

  const createSmartFolder = useCallback(
    async (req: CreateSmartFolderRequest): Promise<MySmartFolder | null> => {
      try {
        return await mine.createSmartFolder(req);
      } catch (err) {
        setDropProblem(err instanceof Error ? err.message : "That search couldn't be kept.");
        return null;
      }
    },
    [mine],
  );

  const tag = useCallback(
    (payload: MaterialPayload, delta: TagDelta) => {
      if ("mine" in payload) void mine.updateClip(payload.mine, { tags: delta });
      else if ("mineMidi" in payload) void mine.updateMidi(payload.mineMidi, { tags: delta });
      track("library_tag_set", { detail: delta.set?.length ? "set" : "unset", value: (delta.set?.length ?? 0) + (delta.unset?.length ?? 0) });
    },
    [mine],
  );

  const tagMany = useCallback(
    (payloads: readonly MaterialPayload[], delta: TagDelta) => {
      const clipIds = payloads.flatMap((p) => ("mine" in p ? [p.mine] : []));
      const midiIds = payloads.flatMap((p) => ("mineMidi" in p ? [p.mineMidi] : []));
      if (clipIds.length + midiIds.length === 0) return;
      void mine.tagMany({ clipIds, midiIds, ...delta });
      track("library_tag_set", { detail: "bulk", value: clipIds.length + midiIds.length });
    },
    [mine],
  );

  /** Every waiting suggestion on the rows (or only the named tags), kept or dismissed. */
  const review = useCallback(
    (payloads: readonly MaterialPayload[], keep: boolean, tags?: readonly string[]) => {
      const clipIds = payloads.flatMap((p) => ("mine" in p ? [p.mine] : []));
      const midiIds = payloads.flatMap((p) => ("mineMidi" in p ? [p.mineMidi] : []));
      if (clipIds.length + midiIds.length === 0) return;
      // The server takes 500 rows at a time; a select-all over more goes in parts.
      for (let i = 0; i < Math.max(clipIds.length, midiIds.length); i += 500) {
        void mine.review({ clipIds: clipIds.slice(i, i + 500), midiIds: midiIds.slice(i, i + 500), keep, ...(tags ? { tags: [...tags] } : {}) });
      }
      track("library_reviewed", { detail: keep ? "keep" : "dismiss", value: clipIds.length + midiIds.length });
    },
    [mine],
  );

  const updateClip = useCallback(
    (id: string, req: UpdateClipRequest) => {
      void mine.updateClip(id, req);
    },
    [mine],
  );

  /** A library row's name, changed in place. */
  const rename = useCallback(
    (payload: MaterialPayload, name: string) => {
      const clean = name.trim();
      if (clean === "") return;
      if ("mine" in payload) void mine.updateClip(payload.mine, { name: clean });
      else if ("mineMidi" in payload) void mine.updateMidi(payload.mineMidi, { name: clean });
      track("library_renamed", { value: 1 });
    },
    [mine],
  );

  /** Live's Collections: one colour on one or many rows (null clears). */
  const color = useCallback(
    (payloads: readonly MaterialPayload[], value: number | null) => {
      for (const p of payloads) {
        if ("mine" in p) void mine.updateClip(p.mine, { color: value });
        else if ("mineMidi" in p) void mine.updateMidi(p.mineMidi, { color: value });
      }
      track("library_colored", { detail: value === null ? "clear" : String(value), value: payloads.length });
    },
    [mine],
  );

  const star = useCallback(
    (payload: MaterialPayload, starred: boolean) => {
      if ("mine" in payload) void mine.updateClip(payload.mine, { starred });
      else if ("mineMidi" in payload) void mine.updateMidi(payload.mineMidi, { starred });
    },
    [mine],
  );


  const library: MineLibrary = {
    signedIn: mine.signedIn,
    clips: mine.clips,
    midi: mine.midi,
    folders: mine.folders,
    smartFolders: mine.smartFolders,
    onCreateSmartFolder: createSmartFolder,
    onUpdateSmartFolder: (id, req) => void mine.updateSmartFolder(id, req),
    onDeleteSmartFolder: (id) => void mine.deleteSmartFolder(id),
    usedBytes: mine.usedBytes,
    quotaBytes: mine.quotaBytes,
    loading: mine.loading,
    error: mine.error instanceof Error ? mine.error.message : mine.error ? String(mine.error) : null,
    onRetry: mine.retry,
    onSignIn: () => gate.openSignIn(),
    onDelete: (id) => void mine.remove(id),
    onDeleteMidi: (id) => void mine.removeMidi(id),
    onStar: star,
    onRename: rename,
    onColor: color,
    onTouch: (key) => {
      if (key.startsWith("mine:") || key.startsWith("midi:")) mine.touch(key);
    },
    recent: mine.recent,
    usesOf: (id) => mine.usesOf(id),
    onUndoBatch: (batchId) => void mine.undoBatch(batchId),
    onTag: tag,
    onTagMany: tagMany,
    onReview: review,
    onSuggest: suggestMany,
    folderTags: (folderId) => folderTags(mine.folders, folderId),
    onUpdateClip: updateClip,
    onMove: moveItems,
    onCreateFolder: createFolder,
    onRenameFolder: (id, name) => void mine.updateFolder(id, { name }),
    onMoveFolder: (id, parentId) => {
      mine.updateFolder(id, { parentId }).catch((err: unknown) => {
        setDropProblem(err instanceof Error ? err.message : "That folder couldn't be moved.");
      });
    },
    onDeleteFolder: (id) => void mine.deleteFolder(id),
    removing: mine.removing ?? null,
    onAddFiles: sender.addFiles,
    onAddPicked: sender.addPicked,
    adding: sender.adding,
    usage: mine.signedIn && mine.quotaBytes > 0 ? usageText(mine.usedBytes, mine.quotaBytes) : null,
    seeds,
    onRenameTag: (from, to) => {
      void mine.renameTag({ from, to });
      track("library_tag_renamed", { detail: "rename", value: 1 });
    },
    onKeepReference: (seedId, audioKey, folderId) => {
      const midiKey = referenceMidiOf(audioKey);
      if (midiKey !== null) void keepMidi(seedId, midiKey, folderId);
      else void keep({ seedId, audioKey, folderId });
    },
  };

  return { mine, library, setDropProblem, keep, keepMidi, sender };
}
